import React, { useEffect } from 'react';
import { Check, Globe, Key, FileKey, Code, Webhook, AlertCircle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { TemplateFormData } from '../TemplateWizard';

interface ReviewStepProps {
  data: TemplateFormData;
  onValidate: (isValid: boolean) => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  onValidate,
}) => {
  // Validate all required fields
  useEffect(() => {
    const isValid = !!(
      data.name.trim() &&
      data.type &&
      data.version.trim() &&
      data.baseUrl.trim() &&
      data.authConfig.type
    );
    onValidate(isValid);
  }, [data, onValidate]);

  const getTypeLabel = (type: string) => {
    const types: Record<string, string> = {
      REST: 'REST API',
      GRAPHQL: 'GraphQL',
      EVM: 'EVM Blockchain',
      SOLANA: 'Solana',
      DEX: 'DEX',
    };
    return types[type] || type;
  };

  const getAuthLabel = (type: string) => {
    const types: Record<string, string> = {
      BEARER: 'Bearer Token',
      BASIC: 'Basic Auth',
      API_KEY: 'API Key',
      OAUTH2: 'OAuth 2.0',
      CUSTOM_HEADER: 'Header Customizado',
    };
    return types[type] || type;
  };

  const getSignatureLabel = (type: string) => {
    const types: Record<string, string> = {
      HMAC_SHA256: 'HMAC-SHA256',
      HMAC_SHA1: 'HMAC-SHA1',
      RSA: 'RSA Signature',
      NONE: 'Sem validação',
    };
    return types[type] || type;
  };

  // Check for potential issues
  const warnings: string[] = [];
  if (data.credentialSchema.length === 0) {
    warnings.push('Nenhum campo de credencial definido');
  }
  if (data.operations.length === 0) {
    warnings.push('Nenhuma operação definida');
  }
  if (data.webhookConfig.enabled && data.webhookConfig.eventMappings.length === 0) {
    warnings.push('Webhooks habilitados mas nenhum evento mapeado');
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Revisão do Template</h2>
        <p className="text-sm text-surface-400">
          Revise todas as configurações antes de salvar o template
        </p>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-yellow-400 mb-1">Atenção</p>
              <ul className="text-xs text-yellow-300/80 space-y-1">
                {warnings.map((warning, index) => (
                  <li key={index}>• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Basic Info */}
        <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-primary-400" />
            <h3 className="text-sm font-medium text-white">Informações Básicas</h3>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-surface-400">Nome:</dt>
              <dd className="text-white font-medium">{data.name || '-'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-surface-400">Tipo:</dt>
              <dd className="text-white">{getTypeLabel(data.type)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-surface-400">Versão:</dt>
              <dd className="text-white font-mono">{data.version || '-'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-surface-400">URL Base:</dt>
              <dd className="text-white font-mono text-xs truncate max-w-[200px]" title={data.baseUrl}>
                {data.baseUrl || '-'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Authentication */}
        <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <div className="flex items-center gap-2 mb-3">
            <Key className="w-4 h-4 text-primary-400" />
            <h3 className="text-sm font-medium text-white">Autenticação</h3>
          </div>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-surface-400">Tipo:</dt>
              <dd className="text-white">{getAuthLabel(data.authConfig.type)}</dd>
            </div>
            {data.authConfig.headerName && (
              <div className="flex justify-between">
                <dt className="text-surface-400">Header:</dt>
                <dd className="text-white font-mono">{data.authConfig.headerName}</dd>
              </div>
            )}
            {data.authConfig.headerPrefix && (
              <div className="flex justify-between">
                <dt className="text-surface-400">Prefixo:</dt>
                <dd className="text-white font-mono">{data.authConfig.headerPrefix}</dd>
              </div>
            )}
            {data.authConfig.oauth2Config?.tokenUrl && (
              <div className="flex justify-between">
                <dt className="text-surface-400">Token URL:</dt>
                <dd className="text-white font-mono text-xs truncate max-w-[200px]">
                  {data.authConfig.oauth2Config.tokenUrl}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Credential Schema */}
        <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <div className="flex items-center gap-2 mb-3">
            <FileKey className="w-4 h-4 text-primary-400" />
            <h3 className="text-sm font-medium text-white">Schema de Credenciais</h3>
          </div>
          {data.credentialSchema.length === 0 ? (
            <p className="text-sm text-surface-500 italic">Nenhum campo definido</p>
          ) : (
            <div className="space-y-2">
              {data.credentialSchema.map((field, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-surface-300">{field.label}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-surface-500 bg-surface-700 px-2 py-0.5 rounded">
                      {field.type}
                    </span>
                    {field.required && (
                      <span className="text-xs text-red-400">*</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Operations */}
        <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <div className="flex items-center gap-2 mb-3">
            <Code className="w-4 h-4 text-primary-400" />
            <h3 className="text-sm font-medium text-white">Operações</h3>
          </div>
          {data.operations.length === 0 ? (
            <p className="text-sm text-surface-500 italic">Nenhuma operação definida</p>
          ) : (
            <div className="space-y-2">
              {data.operations.map((op, index) => (
                <div key={index} className="flex items-center gap-2 text-sm">
                  <span className={cn(
                    'px-1.5 py-0.5 text-xs font-mono rounded',
                    op.method === 'GET' && 'bg-green-500/20 text-green-400',
                    op.method === 'POST' && 'bg-blue-500/20 text-blue-400',
                    op.method === 'PUT' && 'bg-yellow-500/20 text-yellow-400',
                    op.method === 'DELETE' && 'bg-red-500/20 text-red-400',
                    op.method === 'PATCH' && 'bg-purple-500/20 text-purple-400',
                  )}>
                    {op.method}
                  </span>
                  <span className="text-surface-300">{op.name}</span>
                  <span className="text-xs text-surface-500 font-mono truncate">
                    {op.endpoint}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Webhooks */}
      <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
        <div className="flex items-center gap-2 mb-3">
          <Webhook className="w-4 h-4 text-primary-400" />
          <h3 className="text-sm font-medium text-white">Webhooks</h3>
          <span className={cn(
            'px-2 py-0.5 text-xs rounded',
            data.webhookConfig.enabled
              ? 'bg-green-500/20 text-green-400'
              : 'bg-surface-700 text-surface-400'
          )}>
            {data.webhookConfig.enabled ? 'Habilitado' : 'Desabilitado'}
          </span>
        </div>
        
        {data.webhookConfig.enabled && (
          <div className="space-y-3">
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-surface-400">Validação:</dt>
                <dd className="text-white">{getSignatureLabel(data.webhookConfig.signatureValidation)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-surface-400">Header:</dt>
                <dd className="text-white font-mono">{data.webhookConfig.signatureHeader}</dd>
              </div>
            </dl>

            {data.webhookConfig.eventMappings.length > 0 && (
              <div className="pt-2 border-t border-surface-700">
                <p className="text-xs text-surface-400 mb-2">Eventos mapeados:</p>
                <div className="space-y-1">
                  {data.webhookConfig.eventMappings.map((event, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-surface-300 font-mono">{event.providerEvent}</span>
                      <span className="text-surface-500">→</span>
                      <span className="text-primary-400 font-mono">{event.hubEvent}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Description */}
      {data.description && (
        <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
          <h3 className="text-sm font-medium text-white mb-2">Descrição</h3>
          <p className="text-sm text-surface-300">{data.description}</p>
        </div>
      )}

      {/* Confirmation */}
      <div className="flex items-center gap-3 p-4 bg-primary-500/10 border border-primary-500/30 rounded-lg">
        <Check className="w-5 h-5 text-primary-400" />
        <div>
          <p className="text-sm font-medium text-white">Pronto para salvar</p>
          <p className="text-xs text-surface-400">
            Clique em "Criar Template" para finalizar a configuração
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;
